import { forwardRef, useState } from "react";
import { Box, BoxProps, Skeleton, SkeletonBox } from "@hightouch/htui";

/**
 * Recognize the video extensions emitted by the catalog migration's
 * mirrorProductImages (VIDEO_EXTENSIONS in catalog-artifact-import.ts).
 * Exported so callers that thread `disableVideoControls` or otherwise need
 * to branch UI on video-vs-image share one definition with `HTImage`.
 */
const VIDEO_SRC_REGEX = /\.(mp4|mov|webm|avi|mkv)(?:\?|#|$)/i;
export const isVideoSrc = (src: string | undefined | null): boolean =>
  !!src && VIDEO_SRC_REGEX.test(src);

type BaseImageProps = Omit<BoxProps, "alt"> & {
  src: string;
  loading?: HTMLImageElement["loading"];
};

type ImageWithAlt = BaseImageProps & {
  alt: string;
  decorative?: never;
};

type DecorativeImage = BaseImageProps & {
  decorative: true;
  alt?: never;
};

type ImageProps = (ImageWithAlt | DecorativeImage) & {
  disableVideoControls?: boolean;
  /** Show a skeleton placeholder while the image is loading */
  showSkeleton?: boolean;
  /** Height of the skeleton placeholder (defaults to 200px) */
  skeletonHeight?: BoxProps["height"];
};

export const HTImage = forwardRef<HTMLImageElement, ImageProps>(
  function HTImage(
    {
      alt,
      decorative,
      disableVideoControls,
      showSkeleton,
      skeletonHeight = "200px",
      onLoad,
      onError,
      ...props
    },
    ref,
  ) {
    const [isLoading, setIsLoading] = useState(showSkeleton ?? false);
    const [hasError, setHasError] = useState(false);

    const isVideo = isVideoSrc(props?.src);

    const handleLoad: React.ReactEventHandler = (e) => {
      setIsLoading(false);
      onLoad?.(e as React.SyntheticEvent<HTMLDivElement>);
    };

    const handleError: React.ReactEventHandler = (e) => {
      setIsLoading(false);
      setHasError(true);
      onError?.(e as React.SyntheticEvent<HTMLDivElement>);
    };

    if (isVideo) {
      return (
        <>
          {showSkeleton && isLoading && (
            <Skeleton isLoading>
              <SkeletonBox height={skeletonHeight} width="100%" />
            </Skeleton>
          )}
          <Box
            {...props}
            as="video"
            playsInline
            controls={!disableVideoControls}
            src={props.src}
            onLoadedData={handleLoad}
            onError={handleError}
            {...(showSkeleton && {
              height: isLoading ? 0 : (props.height ?? "auto"),
              opacity: isLoading ? 0 : 1,
              transition: "opacity 0.3s ease-in-out",
            })}
          />
        </>
      );
    }

    return (
      <>
        {showSkeleton && isLoading && !hasError && (
          <Skeleton isLoading>
            <SkeletonBox height={skeletonHeight} width="100%" />
          </Skeleton>
        )}
        <Box
          ref={ref}
          as="img"
          alt={decorative ? "" : alt}
          onLoad={handleLoad}
          onError={handleError}
          {...props}
          {...(showSkeleton && {
            height: isLoading ? 0 : (props.height ?? "auto"),
            opacity: isLoading ? 0 : 1,
            transition: "opacity 0.3s ease-in-out",
          })}
        />
      </>
    );
  },
);
